/**
 * Map FiltersDrawer values to existing MI backend query contracts.
 * Drawer multi-select uses a record-separator internally; POST APIs use string arrays via toApiFilterArray.
 */

/** Internal drawer delimiter — must not appear in filter option labels. */
export const MULTI_SELECT_DELIM = '\u001e';

function parseMultiSelectFromCatalog(str, catalog) {
  const sorted = [...catalog].sort((a, b) => b.length - a.length);
  const found = [];
  let i = 0;
  while (i < str.length) {
    const skip = str.slice(i).match(/^[\s,]+/);
    if (skip) {
      i += skip[0].length;
      continue;
    }
    if (i >= str.length) break;

    let matched = null;
    for (const name of sorted) {
      if (str.slice(i, i + name.length) === name) {
        matched = name;
        break;
      }
    }
    if (matched) {
      found.push(matched);
      i += matched.length;
    } else {
      const nextComma = str.indexOf(',', i);
      i = nextComma === -1 ? str.length : nextComma + 1;
    }
  }
  if (found.length) return [...new Set(found)];

  const norm = (n) => String(n || '').trim().toLowerCase();
  const byNorm = new Map(catalog.map((n) => [norm(n), n]));
  if (byNorm.has(norm(str))) return [byNorm.get(norm(str))];

  return str.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Parse a drawer multi-select string into option values.
 * When catalog is provided, legacy comma-separated strings are resolved against known labels
 * (e.g. "Insurance Services Office, Inc." is not split on the comma).
 */
export function parseMultiSelect(value, defaultValue, catalog = null) {
  if (!value || value === defaultValue) return [];
  const str = String(value);
  if (str.includes(MULTI_SELECT_DELIM)) {
    return str.split(MULTI_SELECT_DELIM).map((s) => s.trim()).filter(Boolean);
  }
  if (Array.isArray(catalog) && catalog.length > 0) {
    return parseMultiSelectFromCatalog(str, catalog);
  }
  if (str.includes(', ')) {
    return str.split(', ').map((s) => s.trim()).filter(Boolean);
  }
  return str.split(',').map((s) => s.trim()).filter(Boolean);
}

export function serializeMultiSelect(values) {
  return (Array.isArray(values) ? values : []).filter(Boolean).join(MULTI_SELECT_DELIM);
}

export function toApiScalarFilter(value, defaultValue) {
  if (!value || value === defaultValue) return value;
  const parts = parseMultiSelect(value, defaultValue);
  return parts[0] || value;
}

/** Multi-select as string array for POST APIs (snapshots, insights filters). */
export function toApiFilterArray(value, defaultValue, catalog = null) {
  return parseMultiSelect(value, defaultValue, catalog);
}

/** Legacy comma-separated string (GET query params). */
export function toApiMultiFilter(value, defaultValue, catalog = null) {
  const items = toApiFilterArray(value, defaultValue, catalog);
  return items.length ? items.join(',') : '';
}

/** Add key only when the filter array is non-empty. */
export function assignFilterArray(target, key, value, defaultValue, catalog = null) {
  const items = toApiFilterArray(value, defaultValue, catalog);
  if (items.length) {
    target[key] = items;
  }
  return target;
}

/**
 * Top Competitor page: map drawer company filter to catalog names.
 * "All companies" → full catalog; otherwise intersection with catalog only.
 */
export function resolveActiveCompetitorNames(filterCompany, catalogNames, defaultValue = 'All companies') {
  const catalog = Array.isArray(catalogNames) ? catalogNames.filter(Boolean) : [];
  if (!catalog.length) return [];
  if (!filterCompany || filterCompany === defaultValue) return catalog;
  const norm = (n) => String(n || '').trim().toLowerCase();
  const byNorm = new Map(catalog.map((name) => [norm(name), name]));
  const selected = parseMultiSelect(filterCompany, defaultValue, catalog);
  return selected.map((name) => byNorm.get(norm(name))).filter(Boolean);
}

/** Snapshots POST: company multi-select as string[]. */
export function toApiCompanyFilter(value, defaultValue, catalog = null) {
  const parts = toApiFilterArray(value, defaultValue, catalog);
  return parts.length ? parts : undefined;
}

/** Objections dashboard query: pipe-separated companies (GET). */
export function toApiCompanyQueryParam(value, defaultValue, catalog = null) {
  const parts = toApiFilterArray(value, defaultValue, catalog);
  if (!parts.length) return undefined;
  return parts.join('|');
}
